import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { resolveInteractiveElement } from "../../utils/resolveElement";
import css from "./List.module.css";

const ListRoot = React.forwardRef(
  (
    { as: Component = "ul", dense = false, className, children, ...rest },
    ref,
  ) => (
    <Component
      ref={ref}
      className={clsx(css.root, className)}
      data-dense={dense || undefined}
      {...rest}
    >
      {children}
    </Component>
  ),
);

ListRoot.displayName = "ListRoot";

ListRoot.propTypes = {
  as: PropTypes.oneOf(["ul", "ol", "nav"]),
  dense: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const ListItem = React.forwardRef(
  ({ leading, trailing, secondary, className, children, ...rest }, ref) => (
    <li
      ref={ref}
      className={clsx(css.item, secondary && css.twoLine, className)}
      {...rest}
    >
      {leading && <span className={css.leading}>{leading}</span>}
      <span className={css.text}>
        <span className={css.primary}>{children}</span>
        {secondary && <span className={css.secondary}>{secondary}</span>}
      </span>
      {trailing && <span className={css.trailing}>{trailing}</span>}
    </li>
  ),
);

ListItem.displayName = "ListItem";

ListItem.propTypes = {
  leading: PropTypes.node,
  trailing: PropTypes.node,
  secondary: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

const ListItemButton = React.forwardRef(
  (
    {
      as,
      href,
      selected = false,
      disabled = false,
      leading,
      trailing,
      secondary,
      action,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const Component = resolveInteractiveElement({ as, href, clickable: true });
    const buttonProps =
      Component === "button" ? { type: "button", disabled } : {};
    const linkProps = href
      ? { href, "aria-current": selected ? "page" : undefined }
      : {};

    return (
      <li className={clsx(css.itemButtonWrapper, action && css.withAction)}>
        <Component
          ref={ref}
          className={clsx(
            css.itemButton,
            selected && css.selected,
            secondary && css.twoLine,
            className,
          )}
          data-selected={selected || undefined}
          aria-disabled={disabled || undefined}
          {...buttonProps}
          {...linkProps}
          {...rest}
        >
          {leading && <span className={css.leading}>{leading}</span>}
          <span className={css.text}>
            <span className={css.primary}>{children}</span>
            {secondary && <span className={css.secondary}>{secondary}</span>}
          </span>
          {trailing && <span className={css.trailing}>{trailing}</span>}
        </Component>
        {action && <span className={css.action}>{action}</span>}
      </li>
    );
  },
);

ListItemButton.displayName = "ListItemButton";

ListItemButton.propTypes = {
  as: PropTypes.elementType,
  href: PropTypes.string,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  leading: PropTypes.node,
  trailing: PropTypes.node,
  secondary: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

const ListSubheader = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <li ref={ref} className={clsx(css.subheader, className)} {...rest}>
      {children}
    </li>
  ),
);

ListSubheader.displayName = "ListSubheader";

ListSubheader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ListDivider = React.forwardRef(
  ({ inset = false, className, ...rest }, ref) => (
    <li
      ref={ref}
      role="separator"
      aria-hidden="true"
      className={clsx(css.divider, inset && css.inset, className)}
      {...rest}
    />
  ),
);

ListDivider.displayName = "ListDivider";

ListDivider.propTypes = {
  inset: PropTypes.bool,
  className: PropTypes.string,
};

const List = {
  Root: ListRoot,
  Item: ListItem,
  ItemButton: ListItemButton,
  Subheader: ListSubheader,
  Divider: ListDivider,
};

export default List;
